import React, { useMemo, useState, useEffect } from 'react';
import './App.css';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import HomePage from './components/Pages/HomePage/HomePage';
import AboutPage from './components/Pages/AboutPage/AboutPage';
import FaqPage from './components/Pages/FaqPage/FaqPage';
import NotFoundPage from './components/Pages/NotFoundPage/NotFoundPage';
import ROUTES from './components/common/Routing/Routes';
import { AuthProvider, useAuth } from './components/common/Auth/AuthProvider';
import UnauthorizedRoute from './components/common/Routing/UnauthorizedRoute';
import AuthorizedRoute from './components/common/Routing/AuthorizedRoute';
import ProfilePage from './components/Pages/ProfilePage/ProfilePage';
import RegisterPage from './components/Pages/RegisterPage/RegisterPage';
import PasswordRecoveryPage from './components/Pages/PasswordRecoveryPage/PasswordRecoveryPage';
import { CartProvider } from './contexts/CartContext';
import CartModal from './components/Pages/CartPage/CartModal';
import { Snackbar, Alert, SnackbarCloseReason, CssBaseline, ThemeProvider, createTheme, useMediaQuery } from '@mui/material';
import { setSnackbarCallback } from './components/common/Utils/SnackbarService';
import DetailsPage from './components/Pages/DetailsPage/DetailsPage';
import WishListPage from './components/Pages/WishListPage/WishListPage';
import LoginPage from './components/Pages/LoginPage/LoginPage';
import CheckoutPage from './components/Pages/CheckoutPage/CheckoutPage';
import AdminPage from './components/Pages/AdminPage/AdminPage';
import ListPage from './components/Pages/ListPage/ListPage';
import TopBooksPage from './components/Pages/TopBooksPage/TopBooksPage';
import ClaimAccountPage from './components/Pages/ClaimAccountPage/ClaimAccountPage';
import ShopPage from './components/Pages/ShopPage/ShopPage';
import ShopItemPage from './components/Pages/ShopPage/ShopItemPage';

const AdminOnlyRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthInitialized, user } = useAuth();

  if (!isAuthInitialized) {
    return null;
  }

  return user?.role === 'Admin' ? <>{children}</> : <Navigate to={ROUTES.HOME} replace />;
};

const LOGIN_CONTEXT_MESSAGES: Record<string, string> = {
  [ROUTES.WISH_LIST]: 'Увійдіть або зареєструйтесь, щоб переглянути свій вішліст.',
  [ROUTES.CHECKOUT]: 'Увійдіть або зареєструйтесь, щоб оформити оренду.',
  [ROUTES.ADMIN]: 'Увійдіть, щоб отримати доступ до адмінки.',
};

const LoginRoutePage = () => {
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  const contextMessage = from ? LOGIN_CONTEXT_MESSAGES[from] : undefined;

  return (
    <div className="auth-page default-container">
      <div className="auth-route-card">
        <h1>Вхід</h1>
        {contextMessage && <p className="auth-context-message">{contextMessage}</p>}
        <LoginPage redirectTo={from} />
      </div>
    </div>
  );
};

function App() {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const inputTextColor = prefersDarkMode ? '#eeece5' : '#24251f';
  const inputSurfaceColor = prefersDarkMode ? '#1f221d' : '#fff';
  const theme = useMemo(
    () => createTheme({
      palette: {
        mode: prefersDarkMode ? 'dark' : 'light',
        primary: {
          main: prefersDarkMode ? '#d77a49' : '#b8542b',
          contrastText: prefersDarkMode ? '#21130c' : '#fffaf5',
        },
        secondary: {
          main: prefersDarkMode ? '#dfa070' : '#cc7445',
        },
        background: {
          default: prefersDarkMode ? '#111310' : '#f5f4f0',
          paper: prefersDarkMode ? '#1a1d18' : '#fdfcf9',
        },
        text: {
          primary: prefersDarkMode ? '#eeece5' : '#24251f',
          secondary: prefersDarkMode ? '#aaa99f' : '#696b63',
        },
        divider: prefersDarkMode ? '#383d34' : '#d8d5cd',
      },
      shape: {
        borderRadius: 12,
      },
      typography: {
        fontFamily: 'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        button: {
          fontWeight: 700,
          textTransform: 'none',
        },
      },
      components: {
        MuiInputBase: {
          styleOverrides: {
            input: {
              color: inputTextColor,
              caretColor: inputTextColor,
              WebkitTextFillColor: inputTextColor,
              '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
                WebkitTextFillColor: `${inputTextColor} !important`,
                WebkitBoxShadow: `0 0 0 1000px ${inputSurfaceColor} inset !important`,
                boxShadow: `0 0 0 1000px ${inputSurfaceColor} inset !important`,
                caretColor: `${inputTextColor} !important`,
                transition: 'background-color 9999s ease-out, color 9999s ease-out',
              },
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              color: prefersDarkMode ? '#aaa99f' : '#696b63',
              '&.Mui-focused': {
                color: prefersDarkMode ? '#eeece5' : '#24251f',
              },
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 999,
              boxShadow: 'none',
              paddingInline: 20,
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              backgroundColor: inputSurfaceColor,
            },
            notchedOutline: {
              borderColor: prefersDarkMode ? '#454a41' : '#cbc8c0',
            },
          },
        },
      },
    }),
    [inputSurfaceColor, inputTextColor, prefersDarkMode]
  );

  useEffect(() => {
    setSnackbarCallback((message: string) => {
      setSnackbarMessage(message);
      setSnackbarOpen(true);
    });
  }, []);

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const handleAlertClose = (event: React.SyntheticEvent) => {
    setSnackbarOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <div className="app-container">
              <div className="app">
                <Header />
                <main className="main">
                  <Routes>
                    <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
                    <Route path={ROUTES.HOME} element={<HomePage />} />
                    <Route path={ROUTES.ABOUT} element={<AboutPage />} />
                    <Route path={ROUTES.FAQ} element={<FaqPage />} />
                    <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
                    <Route path={`${ROUTES.DETAILS}/:id`} element={<DetailsPage />} />
                    <Route path={`${ROUTES.LIST}/:slug`} element={<ListPage />} />
                    <Route path={ROUTES.TOP} element={<TopBooksPage />} />
                    <Route path={ROUTES.SHOP} element={<ShopPage />} />
                    <Route path={`${ROUTES.SHOP}/:id`} element={<ShopItemPage />} />
                    <Route path={ROUTES.CLAIM_ACCOUNT} element={<ClaimAccountPage />} />

                  <Route
                    path={ROUTES.LOGIN}
                    element={
                      <UnauthorizedRoute redirectTo={ROUTES.PROFILE}>
                        <LoginRoutePage />
                      </UnauthorizedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.REGISTER}
                    element={
                      // `UnauthorizedRoute` renders its own <Navigate> the instant isAuthenticated
                      // flips true, pre-empting RegisterPage's re-render entirely — so this
                      // redirectTo must match where RegisterPage itself navigates on success
                      // (PROFILE), or the guard's own redirect wins the race and overrides it.
                      <UnauthorizedRoute redirectTo={ROUTES.PROFILE}>
                        <RegisterPage />
                      </UnauthorizedRoute>
                    }
                  />
                  <Route path={ROUTES.PASSWORD_RECOVERY} element={<PasswordRecoveryPage />} />
                  <Route
                    path={ROUTES.PROFILE}
                    element={
                      <AuthorizedRoute redirectTo={ROUTES.HOME}>
                        <ProfilePage />
                      </AuthorizedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.WISH_LIST}
                    element={
                      <AuthorizedRoute redirectTo={ROUTES.LOGIN}>
                        <WishListPage />
                      </AuthorizedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.CHECKOUT}
                    element={
                      <AuthorizedRoute redirectTo={ROUTES.LOGIN}>
                        <CheckoutPage />
                      </AuthorizedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.ADMIN}
                    element={
                      <AuthorizedRoute redirectTo={ROUTES.LOGIN}>
                        <AdminOnlyRoute>
                          <AdminPage />
                        </AdminOnlyRoute>
                      </AuthorizedRoute>
                    }
                  />
                  </Routes>
                </main>
                <Footer />
              </div>
            </div>
            <CartModal />
          </BrowserRouter>
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={4000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert
              severity="error"
              onClose={handleAlertClose}
              sx={{ width: '100%' }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
